import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/** Eine Übungsbestellung, wie der Server sie nennt. */
export type Uebung = { id: number; bestellnr: string };

/**
 * Die eigene Übungsbestellung (A53, #142).
 *
 * WARUM ES DIESEN HAKEN GIBT, obwohl die Tour dieselben Endpunkte ruft:
 * Die Tour holt ihre Nummer imperativ mitten im Ablauf und behält sie in
 * einem Ref. Der Hilfe-Abschnitt braucht sie dagegen als ZUSTAND — er
 * zeigt je nachdem etwas anderes an. Beides aus einem Ref zu bedienen
 * hieße, die Anzeige an den Tour-Ablauf zu hängen.
 *
 * `refetchOnMount: 'always'`, weil die Tour dieselbe Bestellung anlegt
 * und wegräumt, ohne diesen Schlüssel zu kennen: Wer nach einer Tour die
 * Hilfe öffnet, soll den wirklichen Stand sehen und nicht den von vorhin.
 * (Die Tour anzufassen ist in diesem Paket ausdrücklich nicht erlaubt —
 * das hier ist die Antwort darauf, nicht ein Versehen.)
 */
export function useUebung() {
  return useQuery({
    queryKey: ['onboarding', 'uebung'],
    queryFn: () => apiFetch<{ bestellung: Uebung | null }>('/onboarding/uebung.php'),
    staleTime: 0,
    refetchOnMount: 'always'
  });
}

/**
 * Nach jeder Änderung sind drei Dinge alt: dieser Abschnitt, das Brett
 * und die Detailansicht der betroffenen Bestellung. Das Brett zeigt die
 * Übung zwar nicht (api/lib/dashboard.php filtert `ist_uebung = 0`) —
 * aber die ZAHLEN darüber kommen aus derselben Abfrage, und eine
 * gelöschte Bestellung darf in keinem Zwischenspeicher überleben.
 */
function nachladen(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['onboarding', 'uebung'] });
  void qc.invalidateQueries({ queryKey: ['bestellungen'] });
  void qc.invalidateQueries({ queryKey: ['bestellung'] });
}

/**
 * Eine frische Übung anlegen.
 *
 * ACHTUNG, das ist der Grund für die Rückfrage in der Oberfläche: Der
 * Endpunkt LÖSCHT vorher die eigenen Übungen (api/onboarding/uebung.php,
 * `uebungenLoeschen()` vor `uebungAnlegen()`). Eine neue ersetzt die
 * vorhandene — wer das nicht weiß, verliert eine halbfertige Übung ohne
 * Vorwarnung.
 */
export function useUebungAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ bestellung: Uebung }>('/onboarding/uebung.php', {
        method: 'POST',
        body: JSON.stringify({})
      }),
    onSuccess: () => nachladen(qc)
  });
}

/** Die eigenen Übungen entfernen. Der Server antwortet mit der Anzahl. */
export function useUebungEntfernen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ geloescht: number }>('/onboarding/aufraeumen.php', {
        method: 'POST',
        body: JSON.stringify({})
      }),
    onSuccess: () => nachladen(qc)
  });
}
